import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import LockIcon from '@mui/icons-material/Lock';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { PG, pgBtn } from './theme';

export type StudioPlatform = 'easyai' | 'nutrition' | 'sdk';

export interface StudioWizardResult {
  platform: StudioPlatform;
  /** Which Studio mode to land in. Asking and building are different jobs. */
  mode: 'chat' | 'app_builder';
  licenseKey: string;
  targetLanguages: string[];
  starterPrompt: string;
  /** Only set when platform is 'sdk': ios | android | react_native | flutter. */
  sdkPlatform?: string;
}

/** Mirrors SDK_PLATFORMS in Playground.tsx — kept local so this file stays standalone. */
const SDK_TARGETS: { id: string; label: string; langs: string[]; blurb: string }[] = [
  {
    id: 'ios',
    label: 'iOS — Swift',
    langs: ['swift'],
    blurb: 'Swift Package Manager, iOS 13+. Camera work needs a real device, not the Simulator.',
  },
  {
    id: 'android',
    label: 'Android — Kotlin',
    langs: ['kotlin'],
    blurb: 'Maven Central, minSdk 26, compileSdk 35. Built on CameraX and ML Kit.',
  },
  {
    id: 'react_native',
    label: 'React Native — TypeScript',
    langs: ['react', 'typescript'],
    blurb: 'Published to GitHub Packages, not public npm — you need an auth token in .npmrc.',
  },
  {
    id: 'flutter',
    label: 'Flutter — Dart',
    langs: ['flutter'],
    blurb: 'pub.dev package, Flutter 3.3+ and Dart 3.0.6+.',
  },
];

interface StudioWizardProps {
  open: boolean;
  onClose: () => void;
  /** Preselected from URL (`?platform=`). When set, the mode-picker step is skipped. */
  platformHint?: StudioPlatform | null;
  /** License handed off from the Platform accounts page (URL fragment). */
  licensePrefill?: string;
  currentLicense?: string;
  currentTargets?: string[];
  /** Preselected SDK platform, from settings or `?sdk_platform=`. */
  currentSdkPlatform?: string;
  /** The open project already has work, so finishing creates a new project instead of editing it. */
  startsNewProject?: boolean;
  onApply: (result: StudioWizardResult) => void | Promise<void>;
  onOpenBrowseApi: () => void;
  onOpenDocs: () => void;
  onOpenSdkDocs: () => void;
  onOpenSettings: () => void;
}

type TargetOption = {
  id: string;
  label: string;
  langs: string[];
  /** Web targets render in the live canvas; everything else is code you export. */
  preview: boolean;
  blurb: string;
};

export const STUDIO_TARGETS: TargetOption[] = [
  {
    id: 'web',
    label: 'Web app (HTML/CSS/JS)',
    langs: ['html', 'css', 'javascript'],
    preview: true,
    blurb: 'Runs live in the Preview canvas. Best place to start — you see it working immediately.',
  },
  {
    id: 'react',
    label: 'React',
    langs: ['react', 'typescript', 'css'],
    preview: true,
    blurb:
      'Component-based web app. The Preview canvas runs plain browser files, so React only renders live if the app loads React from a CDN — ask for that if you want to watch it work here.',
  },
  {
    id: 'swift',
    label: 'iOS (Swift)',
    langs: ['swift'],
    preview: false,
    blurb: 'Generates Swift you export and open in Xcode. No live canvas preview.',
  },
  {
    id: 'kotlin',
    label: 'Android (Kotlin)',
    langs: ['kotlin'],
    preview: false,
    blurb: 'Generates Kotlin you export into Android Studio. No live canvas preview.',
  },
  {
    id: 'flutter',
    label: 'Flutter / Dart',
    langs: ['flutter'],
    preview: false,
    blurb: 'Cross-platform mobile. Export and run with the Flutter toolchain.',
  },
  {
    id: 'python',
    label: 'Python backend',
    langs: ['python'],
    preview: false,
    blurb: 'Server-side integration — good for batch jobs and your own API layer.',
  },
  {
    id: 'node',
    label: 'Node backend',
    langs: ['node', 'typescript'],
    preview: false,
    blurb: 'Server-side integration in JS/TS.',
  },
];

/** The target a saved `target_languages` list came from, matching exact sets before the lead language. */
export function studioTargetFor(langs?: string[] | null): TargetOption | undefined {
  const list = (langs || []).map((l) => l.toLowerCase());
  if (!list.length) return undefined;
  const key = [...list].sort().join(',');
  return (
    STUDIO_TARGETS.find((t) => [...t.langs].sort().join(',') === key) ||
    STUDIO_TARGETS.find((t) => list.includes(t.langs[0]))
  );
}

/** Opening question for chat mode — a real question, not a build brief. */
function chatStarterPrompt(platform: StudioPlatform, sdkId: string): string {
  if (platform === 'sdk') {
    const label = SDK_TARGETS.find((s) => s.id === sdkId)?.label || 'mobile';
    return [
      `I'm integrating the Passio Nutrition AI SDK into a ${label} app.`,
      'Walk me through it end to end: install, where the key goes, configuring once at startup,',
      'how to know when it is ready, and the minimum code for food search and barcode scanning.',
    ].join(' ');
  }
  if (platform === 'nutrition') {
    return [
      "I'm building against the Passio Nutrition REST API.",
      'Which endpoints should I use to search for a food, get its full nutrition,',
      'and look something up by barcode? Show the requests and what comes back.',
    ].join(' ');
  }
  return 'What can EasyAI do, and how should I structure chains and functions for my use case?';
}

function sdkStarterPrompt(sdkId: string): string {
  const label = SDK_TARGETS.find((s) => s.id === sdkId)?.label || 'mobile';
  return [
    `Scaffold a ${label} app that integrates the Passio Nutrition AI SDK.`,
    '',
    'Requirements:',
    '- Configure the SDK once at startup with remoteOnly = true, and gate the UI until it reports ready.',
    '- A search screen: query foods, list results from nutritionPreview, tap through to detail.',
    '- A detail screen showing calories, protein, carbs and fat, with a serving-size picker.',
    '- A barcode scanning screen using the live camera preview.',
    '- Show configuration and network errors instead of failing silently.',
    '- Read the Passio key from config, never a hard-coded string, and note where to put it in the README.',
  ].join('\n');
}

function starterPromptFor(platform: StudioPlatform, target: TargetOption | undefined): string {
  const t = target?.label || 'web app';
  if (platform === 'nutrition') {
    return [
      `Build a ${t} that lets someone search for a food, pick a result, and see its nutrition.`,
      '',
      'Requirements:',
      '- Three states: search input, loading, and results.',
      '- Use the NutritionAI routes I pinned (do not guess endpoint paths).',
      '- Show calories, protein, carbs, and fat for the selected portion.',
      '- Let the user change the portion size and recalculate.',
      '- Clean, mobile-friendly layout.',
    ].join('\n');
  }
  return [
      `Build a ${t} around the chain I attached.`,
      '',
      'Requirements:',
      '- Three states: input form, loading, and results.',
      '- Send the form values to the attached chain and render the response fields.',
      '- Handle and display errors instead of failing silently.',
      '- Clean, mobile-friendly layout.',
  ].join('\n');
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontWeight: 800, mb: 0.4 }}>{children}</Typography>
);

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.6 }}>
    {children}
  </Typography>
);

const ChoiceCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ selected, onClick, icon, title, children }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 1.75,
      cursor: 'pointer',
      bgcolor: selected ? 'rgba(56,189,248,0.10)' : PG.chat,
      border: '1px solid',
      borderColor: selected ? PG.accent : PG.border,
      borderRadius: 2,
      transition: 'border-color 120ms ease',
      '&:hover': { borderColor: PG.accent },
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: selected ? PG.accent : PG.muted, mt: 0.2 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.3 }}>
          <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
          {selected && <CheckCircleIcon sx={{ fontSize: 16, color: PG.accent }} />}
        </Stack>
        <Body>{children}</Body>
      </Box>
    </Stack>
  </Paper>
);

export const StudioWizard: React.FC<StudioWizardProps> = ({
  open,
  onClose,
  platformHint,
  licensePrefill,
  currentLicense,
  currentTargets,
  currentSdkPlatform,
  startsNewProject,
  onApply,
  onOpenBrowseApi,
  onOpenDocs,
  onOpenSdkDocs,
  onOpenSettings,
}) => {
  const [platform, setPlatform] = useState<StudioPlatform>(platformHint || 'easyai');
  const [license, setLicense] = useState('');
  const [showLicense, setShowLicense] = useState(false);
  const [targetId, setTargetId] = useState('web');
  const [sdkId, setSdkId] = useState('ios');
  const [intent, setIntent] = useState<'chat' | 'app_builder'>('app_builder');
  const [step, setStep] = useState(0);
  const [applying, setApplying] = useState(false);

  // The mode-picker step is dropped entirely when the caller already told us which
  // platform to use (deep link from the Platform accounts page).
  const skipPicker = !!platformHint;

  // Seed values are read through a ref so the reset below depends only on `open`.
  // Depending on the props directly would re-run it on any parent render (the parent
  // passes freshly built arrays), throwing the user back to step 0 mid-walkthrough.
  const seedRef = useRef({ platformHint, licensePrefill, currentLicense, currentTargets, currentSdkPlatform });
  seedRef.current = { platformHint, licensePrefill, currentLicense, currentTargets, currentSdkPlatform };

  useEffect(() => {
    if (!open) return;
    const seed = seedRef.current;
    setStep(0);
    setApplying(false);
    setIntent('app_builder');
    setPlatform(seed.platformHint || 'easyai');
    setLicense(seed.licensePrefill || seed.currentLicense || '');
    const match = studioTargetFor(seed.currentTargets);
    setTargetId(match?.id || 'web');
    setSdkId(SDK_TARGETS.find((s) => s.id === seed.currentSdkPlatform)?.id || 'ios');
  }, [open]);

  const target = useMemo(() => STUDIO_TARGETS.find((t) => t.id === targetId), [targetId]);
  const sdkTarget = useMemo(() => SDK_TARGETS.find((s) => s.id === sdkId), [sdkId]);
  const isNutrition = platform === 'nutrition';
  const isSdk = platform === 'sdk';
  const handedOff = !!licensePrefill && license === licensePrefill;

  const steps = useMemo(() => {
    const list: { key: string; label: string }[] = [];
    if (!skipPicker) list.push({ key: 'platform', label: 'Setup' });
    // The SDK ships a key-per-app rather than a Studio-side license, but it is the same
    // key, so the step stays — with different copy.
    if (isNutrition || isSdk) list.push({ key: 'license', label: 'Key' });
    // Asking and building are different jobs, and a deep link from the accounts page
    // carries no mode — so this is where one launch button covers both.
    list.push({ key: 'intent', label: 'Chat or build' });
    // The SDK pack is per-platform in both modes, so that choice is always needed.
    // Target languages only matter when the model is emitting files.
    if (isSdk) list.push({ key: 'sdkTarget', label: 'SDK' });
    else if (intent === 'app_builder') list.push({ key: 'target', label: 'Target' });
    list.push({ key: 'prompt', label: intent === 'chat' ? 'First question' : 'First prompt' });
    list.push({ key: 'attach', label: isSdk ? 'SDK docs' : isNutrition ? 'Routes' : 'Chains' });
    if (intent === 'app_builder') list.push({ key: 'existing', label: 'Your code' });
    list.push({ key: 'extras', label: 'Good to know' });
    return list;
  }, [skipPicker, isNutrition, isSdk, intent]);

  const activeKey = steps[Math.min(step, steps.length - 1)]?.key;
  const isLast = step >= steps.length - 1;

  const finish = async () => {
    setApplying(true);
    try {
      await onApply({
        platform,
        mode: intent,
        licenseKey: license.trim(),
        targetLanguages: isSdk
          ? sdkTarget?.langs || ['swift']
          : target?.langs || ['html', 'css', 'javascript'],
        starterPrompt:
          intent === 'chat'
            ? chatStarterPrompt(platform, sdkId)
            : isSdk
              ? sdkStarterPrompt(sdkId)
              : starterPromptFor(platform, target),
        ...(isSdk ? { sdkPlatform: sdkId } : {}),
      });
      onClose();
    } finally {
      setApplying(false);
    }
  };

  const renderStep = () => {
    switch (activeKey) {
      case 'platform':
        return (
          <Stack spacing={1.5}>
            <Body>
              Studio has three modes. Pick the one you want — you can switch later with the{' '}
              <strong>PLATFORM</strong> toggle in the header. They stay separate on purpose: each one
              only loads its own knowledge, so the model is not split across all three.
            </Body>
            <ChoiceCard
              selected={platform === 'easyai'}
              onClick={() => setPlatform('easyai')}
              icon={<AutoAwesomeIcon fontSize="small" />}
              title="EasyAI"
            >
              Build an app around <strong>your own</strong> AI chains and functions that you built in Chain
              Builder. Studio wraps them in a real UI. Pick this if you already have a chain, or want to
              design your own AI pipeline.
            </ChoiceCard>
            <ChoiceCard
              selected={platform === 'nutrition'}
              onClick={() => setPlatform('nutrition')}
              icon={<RestaurantIcon fontSize="small" />}
              title="EasyAI + NutritionAI"
            >
              Adds Passio's <strong>NutritionAI</strong> REST API — food search, barcode/scan, and nutrition
              data — on top of EasyAI. Studio injects the live API catalog so the model uses real endpoints.
              Pick this for a <strong>web or backend</strong> food app. Needs a NutritionAI license key.
            </ChoiceCard>
            <ChoiceCard
              selected={platform === 'sdk'}
              onClick={() => setPlatform('sdk')}
              icon={<PhoneIphoneIcon fontSize="small" />}
              title="Mobile SDK"
            >
              For a <strong>native app</strong> that embeds the Passio SDK — iOS, Android, React Native or
              Flutter — instead of calling the REST API. Loads install, configure, camera, barcode and the
              food data model for your platform. Use it to ask integration questions or to scaffold the
              integration.
            </ChoiceCard>
          </Stack>
        );

      case 'intent':
        return (
          <Stack spacing={1.5}>
            <Body>
              Two ways to use Studio with the same knowledge loaded. You can switch any time with the{' '}
              <strong>MODE</strong> toggle in the header — this just sets where you start.
            </Body>
            <ChoiceCard
              selected={intent === 'chat'}
              onClick={() => setIntent('chat')}
              icon={<AutoAwesomeIcon fontSize="small" />}
              title="Chat — ask questions"
            >
              {isSdk ? (
                <>
                  Ask how to integrate: which method to call, where the key goes, why{' '}
                  <code>isReadyForDetection</code> is false, how to handle the camera. You get real method
                  names and code snippets for your platform, not an app.
                </>
              ) : isNutrition ? (
                <>
                  Ask which <strong>endpoint</strong> to use for what you're doing, what the request and
                  response look like, and how to combine calls. The live route catalog is loaded, so answers
                  use real paths.
                </>
              ) : (
                <>Ask how EasyAI works, and how to design chains and functions for your use case.</>
              )}
            </ChoiceCard>
            <ChoiceCard
              selected={intent === 'app_builder'}
              onClick={() => setIntent('app_builder')}
              icon={<VisibilityIcon fontSize="small" />}
              title="App Builder — generate a project"
            >
              Describe a product and the model writes whole files you can preview, iterate on and export.
              {isSdk
                ? ' For the SDK this scaffolds a native project you export to Xcode, Android Studio or the Flutter CLI — it cannot run in the browser canvas.'
                : ' Web targets run live in the Preview canvas.'}
            </ChoiceCard>
          </Stack>
        );

      case 'license':
        return (
          <Stack spacing={1.5}>
            {handedOff ? (
              <Alert
                severity="success"
                icon={<LockIcon fontSize="small" />}
                sx={{ bgcolor: 'rgba(34,197,94,0.10)', color: PG.text }}
              >
                Your NutritionAI license came across from your Passio account — nothing to paste. It was
                handed over in the URL fragment, so it never hit a server log, and it's already been
                cleared from the address bar.
              </Alert>
            ) : (
              <Body>
                {isSdk ? (
                  <>
                    The SDK needs your Passio key (40 characters) from your account page. The{' '}
                    <strong>same key</strong> works for all four SDKs and the REST API, and bills against
                    the same token budget. This is <strong>not</strong> an EasyAI API key.
                  </>
                ) : (
                  <>
                    NutritionAI needs a license key to mint API tokens. You'll find it on your Passio
                    account page. This is <strong>not</strong> an EasyAI API key.
                  </>
                )}
              </Body>
            )}
            <TextField
              size="small"
              fullWidth
              type={showLicense ? 'text' : 'password'}
              autoComplete="off"
              label={isSdk ? 'Passio key' : 'NutritionAI license key'}
              placeholder="Paste the license used to mint Passio tokens"
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowLicense((v) => !v)} sx={{ color: PG.muted }}>
                      {showLicense ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box>
              <SectionTitle>Where it goes</SectionTitle>
              {isSdk ? (
                <Body>
                  Generated code will <strong>never</strong> contain your real key — it emits{' '}
                  <code>YOUR_PASSIO_KEY</code> and a README pointing at the right place for your
                  platform (an <code>xcconfig</code>, <code>local.properties</code>, <code>.env</code>, or{' '}
                  <code>--dart-define</code>). For production, ask about <strong>proxy mode</strong>:
                  set <code>proxyUrl</code> and the key lives on your backend instead of shipping in
                  the app.
                </Body>
              ) : (
                <Body>
                  The key is stored on this Studio session, server-side. When you hit Preview, the
                  <strong> server</strong> exchanges it for a token and proxies the Nutrition calls — so the
                  key is never written into the app files the model generates, and never exposed to the
                  preview iframe. Your exported app supplies its own key via{' '}
                  <code>window.PASSIO_CONFIG</code>.
                </Body>
              )}
            </Box>
            {!license.trim() && (
              <Alert severity="info" sx={{ bgcolor: 'rgba(56,189,248,0.10)', color: PG.text }}>
                {isSdk
                  ? 'You can continue without it — the walkthrough and code generation work either way. You just cannot run the app against Passio until the key is in your project.'
                  : "You can continue without it — chat and code generation still work. You'll just need to paste it before Preview can call the Nutrition API for real."}
              </Alert>
            )}
          </Stack>
        );

      case 'sdkTarget':
        return (
          <Stack spacing={1.5}>
            <Body>
              Which SDK are you integrating? Only the platform you pick gets loaded, so the model
              focuses on your language instead of hedging across four. You can select more than one
              later in Settings.
            </Body>
            <Stack spacing={1}>
              {SDK_TARGETS.map((s) => (
                <ChoiceCard
                  key={s.id}
                  selected={sdkId === s.id}
                  onClick={() => setSdkId(s.id)}
                  icon={<PhoneIphoneIcon fontSize="small" />}
                  title={s.label}
                >
                  {s.blurb}
                </ChoiceCard>
              ))}
            </Stack>
            <Alert severity="info" sx={{ bgcolor: 'rgba(56,189,248,0.10)', color: PG.text }}>
              All four wrap the same backend and expose nearly the same API, so the concepts carry
              over — only the method names and types differ.
            </Alert>
          </Stack>
        );

      case 'target':
        return (
          <Stack spacing={1.5}>
            <Body>
              What are you building? This sets the languages the model writes in. Web targets render live
              in the Preview canvas; mobile and backend targets generate code you export.
            </Body>
            <Stack spacing={1}>
              {STUDIO_TARGETS.map((t) => (
                <ChoiceCard
                  key={t.id}
                  selected={targetId === t.id}
                  onClick={() => setTargetId(t.id)}
                  icon={t.preview ? <VisibilityIcon fontSize="small" /> : <ContentPasteIcon fontSize="small" />}
                  title={t.label}
                >
                  {t.blurb}
                </ChoiceCard>
              ))}
            </Stack>
          </Stack>
        );

      case 'prompt':
        return (
          <Stack spacing={1.5}>
            <Box>
              <SectionTitle>What makes a good first prompt</SectionTitle>
              <Body>
                Describe the <strong>product</strong>, not the code. The model writes whole files, so be
                concrete about screens and data. A strong first prompt names:
              </Body>
            </Box>
            <Stack spacing={0.75} sx={{ pl: 0.5 }}>
              {[
                'The screens / states you want (input → loading → results is a good default).',
                'Which fields go in, and which fields to show in the output.',
                isSdk
                  ? 'Which SDK flow you want: search, barcode scanning, photo recognition, or voice logging.'
                  : isNutrition
                    ? 'Which NutritionAI routes to use — pin them first so the model gets the real contract.'
                    : 'Which chain or function backs it — attach it first with @.',
                'Look and feel: light/dark, brand colors, mobile-first.',
                'What to do on error or empty results.',
              ].map((line) => (
                <Stack key={line} direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ color: PG.accent, fontWeight: 800, lineHeight: 1.6 }}>•</Box>
                  <Body>{line}</Body>
                </Stack>
              ))}
            </Stack>
            <Divider sx={{ borderColor: PG.border }} />
            <Box>
              <SectionTitle>Then iterate in small steps</SectionTitle>
              <Body>
                Ask for one change at a time — "make the results a card grid", "add a portion selector".
                Every reply is a <strong>restore point</strong>, so you can roll the canvas back without
                losing the chat. You can queue a follow-up while a reply is still streaming.
              </Body>
            </Box>
            <Box>
              <SectionTitle>We'll drop this in the composer for you</SectionTitle>
              <Paper
                elevation={0}
                sx={{
                  p: 1.25,
                  bgcolor: PG.chat,
                  border: '1px solid',
                  borderColor: PG.border,
                  borderRadius: 1.5,
                  maxHeight: 190,
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{ color: PG.muted, m: 0, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}
                >
                  {isSdk ? sdkStarterPrompt(sdkId) : starterPromptFor(platform, target)}
                </Typography>
              </Paper>
              <Body>Edit it before sending — it's a starting point, not a template you must keep.</Body>
            </Box>
          </Stack>
        );

      case 'attach':
        if (isSdk) {
          return (
            <Stack spacing={1.5}>
              <Box>
                <SectionTitle>The docs are already loaded</SectionTitle>
                <Body>
                  You don't have to manage any of this. Studio ships a knowledge pack for{' '}
                  {sdkTarget?.label || 'your platform'} covering install, configure, camera, barcode and
                  the data model, and on top of that it <strong>searches the full official Passio
                  documentation on every message</strong> and pulls in the passages that match what you
                  asked — the exact method signature, its parameters and what it returns.
                </Body>
                <Body>
                  So just ask normally. "How do I scan a barcode?" is enough; you don't need to know
                  which page the answer lives on, or say any magic words.
                </Body>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" size="small" sx={pgBtn} onClick={onOpenSdkDocs}>
                  Browse SDK Docs
                </Button>
              </Stack>
              <Box>
                <SectionTitle>Pinning, if you want it</SectionTitle>
                <Body>
                  Optional. Pinning a page guarantees it is included whole rather than by relevance —
                  useful when you're working through one page end to end, like the{' '}
                  <strong>Quick Start Guide</strong>. Up to 6 pages, applied to your{' '}
                  <strong>next</strong> message, so pin before you send.
                </Body>
              </Box>
              <Alert severity="warning" sx={{ bgcolor: 'rgba(245,158,11,0.10)', color: PG.text }}>
                The v2 SDK did real-time on-device detection and is fully deprecated. If you paste old
                code using <code>startFoodDetection</code>, ask for a port rather than an extension —
                v3 is photo-and-cloud based.
              </Alert>
            </Stack>
          );
        }
        return isNutrition ? (
          <Stack spacing={1.5}>
            <Box>
              <SectionTitle>Pin the routes you need (do this first)</SectionTitle>
              <Body>
                The model gets a <strong>short index</strong> of every live NutritionAI route, but only the
                full request/response contract for routes you pin. Pinning is what stops it from inventing
                endpoint paths or body shapes.
              </Body>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" size="small" sx={pgBtn} onClick={onOpenBrowseApi}>
                Open Browse API
              </Button>
              <Button variant="outlined" size="small" sx={pgBtn} onClick={onOpenDocs}>
                Open Docs
              </Button>
            </Stack>
            <Box>
              <SectionTitle>Three ways to reference a route</SectionTitle>
              <Body>
                <strong>Browse API</strong> — search live routes, hit <em>Add to chat</em>. That pins the
                route (up to 8) and drops an <code>@alias</code> into your message.
              </Body>
              <Body>
                <strong>Type @ in the composer</strong> — autocompletes across your chains and NutritionAI
                routes. Arrow keys to move, Enter or Tab to pick.
              </Body>
              <Body>
                <strong>Docs</strong> — the official Passio documentation is searched automatically on
                every message, so this is only for forcing a specific page in whole (up to 6). Typing
                "look up docs" or "passio docs" in chat also pulls matching
                pages.
              </Body>
            </Box>
            <Alert severity="info" sx={{ bgcolor: 'rgba(56,189,248,0.10)', color: PG.text }}>
              Pinned routes and docs are sent with your <strong>next</strong> message, so pin before you
              send, not after.
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Box>
              <SectionTitle>Attach the chain you want a UI for</SectionTitle>
              <Body>
                App Builder wraps something you already built. Attaching a chain sends its graph, steps,
                and input fields along with your prompt, so the generated app posts the right payload.
              </Body>
            </Box>
            <Box>
              <SectionTitle>Two ways to attach</SectionTitle>
              <Body>
                <strong>Type @ in the composer</strong> — autocompletes your chains and functions. Arrow
                keys to move, Enter or Tab to pick.
              </Body>
              <Body>
                <strong>@ chain / Add chain</strong> under the composer opens a picker — no need to open
                Settings.
              </Body>
            </Box>
            <Box>
              <SectionTitle>No chain yet?</SectionTitle>
              <Body>
                Build one in <strong>Chain Builder</strong> first, save it, then come back and attach it —
                or use <strong>Make an app</strong> from the chain itself, which lands you here with it
                already attached.
              </Body>
            </Box>
            <Alert severity="info" sx={{ bgcolor: 'rgba(56,189,248,0.10)', color: PG.text }}>
              Preview runs your attached chains <strong>as you</strong> while you're logged in, so you can
              test for real before exporting.
            </Alert>
          </Stack>
        );

      case 'existing':
        return (
          <Stack spacing={1.5}>
            <Box>
              <SectionTitle>Start from your existing project</SectionTitle>
              <Body>
                You don't have to start from a blank canvas. In the Preview toolbar,{' '}
                <strong>Import project</strong> opens a folder picker and pulls your app in — then just
                describe where the AI should go ("add a nutrition lookup to the search page").
              </Body>
            </Box>
            <Box>
              <SectionTitle>What import accepts</SectionTitle>
              <Body>
                A source folder — HTML, CSS, JS/TS, JSX/TSX, JSON, Markdown, Python, Go, Java, Kotlin,
                Swift, Dart, Vue, Svelte, YAML, SVG and similar. It skips <code>node_modules</code>,{' '}
                <code>.git</code>, <code>dist</code> and <code>build</code>, and caps out around 80 files,
                so trim it to the part you want changed. Images land in <code>assets/</code>.
              </Body>
            </Box>
            <Box>
              <SectionTitle>Getting code back out</SectionTitle>
              <Body>
                <strong>Export</strong> gives you a zip with a START_HERE guide and hosting files. There's
                also a <strong>Cursor pack</strong> if you want to keep building in an editor, and a
                hosting pack for deploys.
              </Body>
              {isSdk && (
                <Body>
                  For SDK work this is the <strong>main</strong> way out: export, then open the project in
                  Xcode, Android Studio, or your React Native / Flutter toolchain and build it there.
                </Body>
              )}
            </Box>
            <Alert severity="warning" sx={{ bgcolor: 'rgba(245,158,11,0.10)', color: PG.text }}>
              Importing over a project that already has files asks first — it replaces the canvas, so
              export anything you want to keep.
            </Alert>
          </Stack>
        );

      case 'extras':
        return (
          <Stack spacing={1.5}>
            {isSdk && (
              <Alert severity="warning" sx={{ bgcolor: 'rgba(245,158,11,0.10)', color: PG.text }}>
                <strong>The Preview canvas cannot run native code.</strong> It only executes
                HTML/CSS/JS, and Studio does not proxy native SDK calls. You'll get real Swift, Kotlin,
                Dart or RN source plus a small mocked <code>preview/index.html</code> so the canvas
                isn't blank — treat that as a UX sketch, not a running app. Build for real after
                exporting.
              </Alert>
            )}
            {isSdk && (
              <Box>
                <SectionTitle>The one thing that breaks most integrations</SectionTitle>
                <Body>
                  Almost every "the SDK returns nothing" report is the SDK not being{' '}
                  <strong>ready</strong> yet. Configure once at startup and wait for{' '}
                  <code>isReadyForDetection</code> before calling anything — most APIs return null or
                  empty rather than throwing. Ask for <code>remoteOnly = true</code> to skip the model
                  download entirely.
                </Body>
              </Box>
            )}
            <Box>
              <SectionTitle>Point at the screen instead of describing it</SectionTitle>
              <Body>
                <strong>Place</strong> — click one element (logo, hero, button) to generate an image or
                describe a change scoped to that spot. <strong>Mark</strong> — drag a box over Preview and
                write notes; it sends a wireframe with your message and works even if the app is currently
                broken.
              </Body>
            </Box>
            <Box>
              <SectionTitle>When Preview breaks</SectionTitle>
              <Body>
                Errors surface as toasts at the bottom of the pane with the request and response.{' '}
                <strong>Ask chat to fix</strong> hands that straight to the model, which is far more
                effective than describing the failure yourself.
              </Body>
            </Box>
            <Box>
              <SectionTitle>Auth, for real</SectionTitle>
              {isSdk ? (
                <Body>
                  The SDK authenticates with your Passio key at <code>configure</code> time — there is no
                  token exchange to write yourself, unlike the REST API. Keep the key in platform config,
                  not source. For production, <strong>proxy mode</strong> (<code>proxyUrl</code> plus
                  optional <code>proxyHeaders</code>) routes every call through your backend so the app
                  ships without a key at all.
                </Body>
              ) : (
                <Body>
                  Generated web apps read <code>window.PASSIO_CONFIG</code> (<code>easyAiBase</code>,{' '}
                  <code>easyAiKey</code>, <code>customerId</code>). In Preview, Studio injects a temporary
                  key and proxies calls server-side as you. When you export, you drop in your own EasyAI API
                  key {isNutrition ? 'and NutritionAI license ' : ''}— the exported files never contain the
                  credentials used in Preview.
                </Body>
              )}
            </Box>
            <Box>
              <SectionTitle>Nothing is lost</SectionTitle>
              <Body>
                Every reply is a restore point in the <strong>Notes</strong> timeline. Rolling back the
                canvas never rewrites your chat. <strong>New app</strong> starts a fresh session and leaves
                the old one alone.
              </Body>
            </Box>
            <Box>
              <SectionTitle>Two small gotchas</SectionTitle>
              <Body>
                The model dropdown applies to your <strong>next</strong> send, not the reply you're looking
                at. And if the canvas stays empty, the model probably didn't emit files with{' '}
                <code>path=</code> fences — just ask it to re-emit them.
              </Body>
            </Box>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: PG.accent }} />
        Get started in Studio
        {isNutrition && (
          <Chip
            size="small"
            label="NutritionAI"
            sx={{ bgcolor: 'rgba(56,189,248,0.14)', color: PG.text, fontWeight: 700 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: PG.border }}>
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{
            mb: 2.5,
            '& .MuiStepLabel-label': { fontSize: 11, color: PG.muted },
            '& .MuiStepLabel-label.Mui-active': { color: PG.text, fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: PG.muted },
          }}
        >
          {steps.map((s) => (
            <Step key={s.key}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStep()}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Tooltip title="Open Studio Settings (model, attachments, license)">
          <Button onClick={onOpenSettings} sx={{ ...pgBtn, color: PG.muted }}>
            Settings
          </Button>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {isLast && startsNewProject && (
          <Typography variant="caption" sx={{ color: PG.muted, mr: 1, textAlign: 'right', lineHeight: 1.35 }}>
            Starts a new project. Your current one stays as it is.
          </Typography>
        )}
        <Button onClick={onClose} sx={{ ...pgBtn, color: PG.muted }}>
          Skip
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => Math.max(0, s - 1))} sx={pgBtn} disabled={applying}>
            Back
          </Button>
        )}
        {isLast ? (
          <Button variant="contained" onClick={finish} disabled={applying} sx={pgBtn}>
            {applying ? 'Setting up…' : 'Start building'}
          </Button>
        ) : (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} sx={pgBtn}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default StudioWizard;
